import React, { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Button } from "src/components/ui/button"
import { Switch } from "src/components/ui/switch"
import { useToast } from "src/components/ui/toast"
import { useRole, useUpdateRolePermissions } from "../hooks/useMembers"

export function RolePermissionsDrawer({ open, role, onClose, onSuccess }) {
  const { toast } = useToast()
  const { role: detail, loading, fetch: fetchRole } = useRole()
  const updateHook = useUpdateRolePermissions()
  const [perms, setPerms] = useState({})

  useEffect(() => {
    if (open && role) fetchRole(role.id)
  }, [open, role, fetchRole])

  useEffect(() => {
    if (detail) setPerms(detail.permissions || {})
  }, [detail])

  // Manage implies Read: enabling Manage turns Read on; disabling Read turns Manage off.
  function setToggle(resource, key, value) {
    setPerms((prev) => {
      const current = { ...(prev[resource] || { read: false, manage: false }) }
      current[key] = value
      if (key === "manage" && value) current.read = true
      if (key === "read" && !value) current.manage = false
      return { ...prev, [resource]: current }
    })
  }

  async function handleSave() {
    try {
      await updateHook.mutate(role.id, perms)
      toast({ title: "Permissions updated", description: role.name })
      onSuccess?.()
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : (errors?.message || "Something went wrong")
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  const resources = detail?.resources || []

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>Edit Permissions — {role?.name}</SheetTitle>
          <SheetDescription>
            Choose what this role can do. <span className="font-medium">Read</span> allows viewing;{" "}
            <span className="font-medium">Manage</span> allows creating, editing and deleting.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && !detail ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="grid grid-cols-[1fr_80px_80px] items-center bg-slate-50 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                <span>Resource</span>
                <span className="text-center">Read</span>
                <span className="text-center">Manage</span>
              </div>
              {resources.map((res, idx) => {
                const value = perms[res.key] || { read: false, manage: false }
                return (
                  <div
                    key={res.key}
                    className={`grid grid-cols-[1fr_80px_80px] items-center px-4 py-3 ${idx > 0 ? "border-t border-slate-100" : ""}`}
                  >
                    <span className="text-[14px] font-medium text-slate-800">{res.label}</span>
                    <div className="flex justify-center">
                      <Switch
                        checked={!!value.read}
                        onCheckedChange={(v) => setToggle(res.key, "read", v)}
                      />
                    </div>
                    <div className="flex justify-center">
                      <Switch
                        checked={!!value.manage}
                        onCheckedChange={(v) => setToggle(res.key, "manage", v)}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button variant="ghost" onClick={onClose} disabled={updateHook.isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={updateHook.isPending || loading} className="gap-2">
            {updateHook.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
